import { rfidTagApi } from "#/api/rfidTagApi";
import { showSuccessNotification } from "#/components/notifications";
import type { RfidTag } from "#/types/rfidTag";
import { RfidTagType } from "#/types/rfidTag";
import { Badge, Button, Divider, Modal, Text } from "@mantine/core";
import { IconBox, IconCar, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

interface BulkDeleteModalProps {
  opened: boolean;
  onClose: () => void;
  selectedTags: RfidTag[];
  onSuccess: () => void;
}

const BulkDeleteModal = ({
  opened,
  onClose,
  selectedTags,
  onSuccess,
}: BulkDeleteModalProps) => {
  const { t } = useTranslation();
  const { canDelete } = usePermission();
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      return await rfidTagApi.bulkDelete(ids);
    },
    onSuccess: (response) => {
      const deleted = response.data.deleted;
      showSuccessNotification({
        message: t("rfidTags.bulkDelete.success", { count: deleted }),
      });
      onClose();
      onSuccess();
    },
    onError: (error) => {
      console.log("🚀 ~ bulkDeleteRfidTags ~ error:", error);
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={t("rfidTags.bulkDelete.title")}
      closeOnClickOutside={false}
      size="xl"
      classNames={{
        title: "font-bold",
      }}
    >
      <div className="w-full flex flex-col gap-4">
        <Text size="sm" c="dimmed">
          {t("rfidTags.bulkDelete.description", { count: selectedTags.length })}
        </Text>

        <div className="w-full max-h-[500px] overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50">
          <div className="grid grid-cols-2 gap-3">
            {selectedTags.map((tag) => (
              <div
                key={tag.id}
                className="flex flex-col gap-2 p-3 bg-white border border-gray-300 rounded-lg"
              >
                <div className="flex items-center justify-between gap-2">
                  <Text
                    size="sm"
                    fw={500}
                    className="font-mono truncate flex-1"
                  >
                    {tag.rfid}
                  </Text>
                  <Badge
                    color={tag.type === RfidTagType.HARD ? "blue" : "orange"}
                    variant="light"
                    size="sm"
                    className="capitalize flex-shrink-0"
                  >
                    {tag.type}
                  </Badge>
                </div>
                {(tag.item || tag.vehicle) && (
                  <>
                    <Divider my={4} />
                    <div className="flex flex-col gap-1">
                      <Text
                        size="10px"
                        c="dimmed"
                        fw={400}
                        tt="uppercase"
                        lh={1}
                      >
                        {t("rfidTags.bulkDelete.linkedTo")}
                      </Text>
                      {tag.item && (
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1">
                            <IconBox
                              size={14}
                              className="text-gray-600 flex-shrink-0"
                            />
                            <Text size="sm" fw={500}>
                              {t("rfidTags.editTag.itemNumber", { id: tag.item.id })}
                            </Text>
                          </div>
                          {tag.item.product?.name && (
                            <Text
                              size="xs"
                              c="dimmed"
                              className="truncate pl-5"
                            >
                              {tag.item.product.name}
                            </Text>
                          )}
                        </div>
                      )}
                      {tag.vehicle && (
                        <div className="flex items-center gap-1">
                          <IconCar
                            size={14}
                            className="text-gray-600 flex-shrink-0"
                          />
                          <Text size="sm" fw={500} className="truncate">
                            {tag.vehicle.name}
                          </Text>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" color="black" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <PermissionTooltip disabled={!canDelete()}>
            <Button
              color="red"
              leftSection={<IconTrash size={18} />}
              onClick={() => {
                bulkDeleteMutation.mutate(selectedTags.map((tag) => tag.id));
              }}
              loading={bulkDeleteMutation.isPending}
              disabled={!canDelete()}
            >
              {t("rfidTags.bulkDelete.deleteButton", { count: selectedTags.length })}
            </Button>
          </PermissionTooltip>
        </div>
      </div>
    </Modal>
  );
};

export default BulkDeleteModal;
